import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router-dom";

import { faCircleInfo, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

/**
 * Dismissal is remembered per user rather than per list (feature definition §4.0), so the key is a
 * single stable one — `localStorage` is already scoped to the signed-in browser profile.
 */
export const OUTREACH_SKIP_BAR_DISMISSED_KEY = "outreachSkipBarDismissed";

const readDismissed = (): boolean => {
	try {
		return window.localStorage.getItem(OUTREACH_SKIP_BAR_DISMISSED_KEY) === "1";
	} catch {
		// Private-mode / blocked storage: show the bar rather than crashing the step.
		return false;
	}
};

const persistDismissed = (): void => {
	try {
		window.localStorage.setItem(OUTREACH_SKIP_BAR_DISMISSED_KEY, "1");
	} catch {
		// Nothing to do — the bar stays dismissed for this session only.
	}
};

interface OutreachSkipBarProps {
	/** The list being edited; the skip target is its settings step. */
	listId: number | string;
}

/**
 * The "this step is optional" bar above the Outreach step's sections (PREC-3687). Offers a shortcut
 * to the Settings step and can be dismissed for good.
 */
export const OutreachSkipBar: FunctionComponent<OutreachSkipBarProps> = ({ listId }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const to = (key: string) => t(`list.outreach.skip.${key}`);

	const [isDismissed, setIsDismissed] = useState<boolean>(readDismissed);

	const handleDismiss = () => {
		persistDismissed();
		setIsDismissed(true);
	};

	if (isDismissed) {
		return null;
	}

	return (
		<div className="mb-4 flex w-full items-center gap-4 rounded-xl border border-border-light bg-light-blue px-5 py-4">
			<Icon className="h-4 w-4 shrink-0 text-brand-light" icon={faCircleInfo} />
			<div className="min-w-0 flex-1">
				<p className="text-sm font-semibold text-text">{to("title")}</p>
				<p className="mt-0.5 text-xs leading-5 text-text-meta">{to("description")}</p>
			</div>
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				size={ButtonSize.XS}
				title={to("action")}
				onClick={() => navigate(`/app/lists/create/settings/${listId}`)}
			/>
			<button
				aria-label={to("dismiss")}
				className="cursor-pointer text-text-meta hover:text-text"
				type="button"
				onClick={handleDismiss}
			>
				<Icon className="h-4 w-4" icon={faXmark} />
			</button>
		</div>
	);
};
